import { FormattedMessage } from "react-intl";
import { BymaActivitiesScrollProps } from "./types";
import { animated } from "@react-spring/web";
import { messages } from "./messages";
import {
  ACCOUNT_PROVIDERS,
  GetMerkActivitiesParams,
  MERK_ACTIVITY_FILTER_BY_DATE,
  MERK_ACTIVITY_TYPE,
  MerkActivity,
} from "@letsbit/malcolmlb";
import classNames from "classnames";
import { Pill } from "components/Pill";
import { ReactComponent as AdjustIcon } from "assets/images/icons/adjust.svg";
import { useEffect, useState } from "react";
import { Drawer } from "antd";
import { BymaActivitiesAdvancedSearch } from "../BymaActivitiesAdvancedSearch";
import { BymaActivitiesDetails } from "../BymaActivitiesDetails";
import { BymaActivitiesScrollReceipt } from "./components/BymaActivitiesScrollReceipt";
import { BymaActivitiesScrollCoupon } from "./components/BymaActivitiesScrollCoupon";
import { MiniLoading } from "components/MiniLoading";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  ACTIVITIES_SCROLL_TYPE,
  useBymaActivitiesScroll,
} from "./useBymaActivitiesScroll";
import { BymaActivitiesScrollCheckingAccountMovement } from "./components/BymaActivitiesScrollCheckingAccountMovement";
import { BymaScrollSeparator } from "./components/BymaScrollSeparator";

import "./styles.scss";

const HISTORY_CARD_HEIGHT = 68;
const SEPARATOR_HEIGHT = 50;

export const BymaActivitiesScroll: React.FC<BymaActivitiesScrollProps> = ({
  header,
  initialFilter,
  noChips,
  chipsAlign = "right",
  headerSticky,
  className,
  memberUid,
}) => {
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filter, setFilter] = useState<GetMerkActivitiesParams>({
    provider: initialFilter?.provider || ACCOUNT_PROVIDERS.ESCO,
    end_date: initialFilter?.end_date,
    start_date: initialFilter?.start_date,
    activity_types:
      initialFilter?.activity_types ||
      MERK_ACTIVITY_TYPE.CHECKING_ACCOUNT_MOVEMENT,
    assets: initialFilter?.assets || [],
    by_date: initialFilter?.by_date || MERK_ACTIVITY_FILTER_BY_DATE.LIQUIDATION,
    page_size: initialFilter?.page_size || "30",
  });
  const [height, setHeight] = useState<number | undefined>();
  const [pageToken, setPageToken] = useState<string | undefined>();

  const [currentActivity, setCurrentActivity] = useState<MerkActivity>();

  const {
    listContainerRef,
    data,
    size,
    setSize,
    forwardedRef,
    itemsTransitions,
    hasMore,
    isLoading,
  } = useBymaActivitiesScroll<HTMLDivElement>(
    HISTORY_CARD_HEIGHT,
    SEPARATOR_HEIGHT,
    { page_token: pageToken, ...filter },
    false,
    memberUid,
  );

  useEffect(() => {
    const observeTarget = listContainerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      // Assuming you want to track the dimensions of the first observed entry
      const { height } = entries[0].contentRect;

      if (height) {
        setHeight(height);
      }
    });

    if (observeTarget) {
      resizeObserver.observe(observeTarget);
    }

    // Cleanup function to unobserve when the component unmounts
    return () => {
      if (observeTarget) {
        resizeObserver.unobserve(observeTarget);
      }
    };
  }, [listContainerRef]);

  return (
    <div
      className="HisotryInfiniteScroll fadeInSimple relative h-full grow"
      ref={listContainerRef}
    >
      {height && (
        <InfiniteScroll
          dataLength={data.length} // This is important field to render the next data
          next={() => {
            const lastItem = data[data.length - 1];
            setPageToken(lastItem?.id); // Use the last item's ID as the page token
            setSize(size + 1);
          }}
          style={{ overflowX: "hidden" }}
          hasMore={hasMore}
          loader={<MiniLoading />}
          height={height}
          className={classNames("list-none p-1", className)}
          endMessage={
            data.length === 0 ? (
              <p
                style={{ textAlign: "center" }}
                className="text-xs font-semibold text-[--milkylb-color-neutral-400]"
              >
                Tu cartera no tiene inversiones por el momento.
                <br />
                <b>Escribíle por whatsapp a tu agente para operar.</b>
              </p>
            ) : (
              <p
                style={{ textAlign: "center" }}
                className="text-xs font-semibold text-[--milkylb-color-secondary-100]"
              >
                Llegaste al final de la lista
              </p>
            )
          }
        >
          {header}
          <div className="sticky -top-1 z-10 flex w-full flex-col">
            <div className="w-[calc(100% + 8px)] -ml-2 flex flex-row items-center justify-between border-b-4 border-l-8 border-r-8 border-t-8 border-white bg-white">
              {headerSticky && (
                <h3 className="mr-2 flex shrink-0 cursor-pointer items-center font-['Tomato_Grotesk'] text-2xl font-bold text-[--milkylb-color-neutral-600]">
                  {headerSticky}
                </h3>
              )}
              <div
                className={classNames(
                  "flex w-full",
                  chipsAlign === "right" ? "justify-end" : "justify-between",
                )}
              >
                {!noChips && (
                  <div className="flex flex-row gap-x-6">
                    <Pill
                      selected={
                        filter.activity_types ===
                        MERK_ACTIVITY_TYPE.CHECKING_ACCOUNT_MOVEMENT
                      }
                      onClick={() =>
                        setFilter({
                          ...filter,
                          activity_types:
                            MERK_ACTIVITY_TYPE.CHECKING_ACCOUNT_MOVEMENT,
                        })
                      }
                    >
                      <FormattedMessage {...messages.checkingAccountPill} />
                    </Pill>
                    <Pill
                      selected={
                        filter.activity_types === MERK_ACTIVITY_TYPE.RECEIPT
                      }
                      onClick={() =>
                        setFilter({
                          ...filter,
                          activity_types: MERK_ACTIVITY_TYPE.RECEIPT,
                        })
                      }
                    >
                      <FormattedMessage {...messages.receiptPill} />
                    </Pill>
                    <Pill
                      selected={
                        filter.activity_types === MERK_ACTIVITY_TYPE.COUPON
                      }
                      onClick={() =>
                        setFilter({
                          ...filter,
                          activity_types: MERK_ACTIVITY_TYPE.COUPON,
                        })
                      }
                    >
                      <FormattedMessage {...messages.couponPill} />
                    </Pill>
                  </div>
                )}
                <AdjustIcon
                  onClick={() => setOpenFilter(true)}
                  className="history__header__icon ml-6 h-8 w-8 cursor-pointer rounded-full p-0.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-5 border-b-4 border-l-8 border-r-8 border-t-8 border-white bg-white p-4">
              <label className="text-sm text-[--milkylb-color-neutral-400]">
                <FormattedMessage {...messages.detailLabel} />
              </label>
              <label className="text-sm text-[--milkylb-color-neutral-400]">
                {filter.by_date === MERK_ACTIVITY_FILTER_BY_DATE.LIQUIDATION ? (
                  <FormattedMessage {...messages.liquidationDateLabel} />
                ) : (
                  <FormattedMessage {...messages.agreementDateLabel} />
                )}
              </label>
              <label className="text-sm text-[--milkylb-color-neutral-400]">
                <FormattedMessage {...messages.amountLabel} />
              </label>
              <label className="text-sm text-[--milkylb-color-neutral-400]">
                <FormattedMessage {...messages.priceLabel} />
              </label>

              <label className="text-sm text-[--milkylb-color-neutral-400]">
                <FormattedMessage {...messages.netValueLabel} />
              </label>
            </div>
            <div className="divider-gradient h-[15px] w-full" />
          </div>
          {isLoading && data.length === 0 && <MiniLoading />}
          {data &&
            itemsTransitions((style, item) => {
              if (item.type === ACTIVITIES_SCROLL_TYPE.ITEM) {
                return (
                  <animated.div style={style}>
                    <div ref={forwardedRef(item.data)} className="pr-2">
                      {item.data.type === MERK_ACTIVITY_TYPE.RECEIPT && (
                        <BymaActivitiesScrollReceipt
                          activity={item.data}
                          onClick={setCurrentActivity}
                          byDate={filter.by_date}
                        />
                      )}
                      {item.data.type === MERK_ACTIVITY_TYPE.COUPON && (
                        <BymaActivitiesScrollCoupon
                          activity={item.data}
                          onClick={setCurrentActivity}
                          byDate={filter.by_date}
                        />
                      )}
                      {item.data.type ===
                        MERK_ACTIVITY_TYPE.CHECKING_ACCOUNT_MOVEMENT && (
                        <BymaActivitiesScrollCheckingAccountMovement
                          activity={item.data}
                          onClick={setCurrentActivity}
                          byDate={filter.by_date}
                        />
                      )}
                    </div>
                  </animated.div>
                );
              } else if (item.type === ACTIVITIES_SCROLL_TYPE.SEPARATOR) {
                return (
                  <animated.div style={style}>
                    <BymaScrollSeparator date={item.data.date} />
                  </animated.div>
                );
              }
            })}
        </InfiniteScroll>
      )}
      <Drawer
        width="100%"
        placement="right"
        keyboard={false}
        closable={false}
        onClose={() => setOpenFilter(false)}
        open={openFilter}
        getContainer=".ion-root"
      >
        <BymaActivitiesAdvancedSearch
          filter={filter}
          setFilter={setFilter}
          onClose={() => setOpenFilter(false)}
          memberUid={memberUid}
        />
      </Drawer>
      <BymaActivitiesDetails
        data={currentActivity}
        onClose={() => setCurrentActivity(undefined)}
      />
    </div>
  );
};
